import { useState, useEffect, useRef, useCallback } from 'react';
import { shallowEqual } from '@utils/react-utils';

/**
 * Custom hook that provides form state management.
 * Handles data syncing from props, modification tracking, and field updates.
 *
 * @param {Object} options - Hook configuration options
 * @param {Object} options.data - The form data from props/redux
 * @param {Function} options.afterReceiveProps - Callback when data changes (optional)
 * @returns {Object} Form state and handlers
 */
export const useForm = ({ data: propData, afterReceiveProps }) => {
  const [modified, setModified] = useState(false);
  const [data, setData] = useState(null);
  const prevDataRef = useRef(propData);

  useEffect(() => {
    if (
      !shallowEqual(propData, prevDataRef.current) ||
      (propData !== null && data === null)
    ) {
      setData(propData);
      if (afterReceiveProps) {
        afterReceiveProps(propData);
      }
    }
    prevDataRef.current = propData;
  }, [propData, data, afterReceiveProps]);

  const onChange = useCallback((field, value) => {
    setModified(true);
    setData(prevData => ({
      ...prevData,
      [field]: value
    }));
  }, []);

  return {
    data,
    setData,
    modified,
    setModified,
    onChange
  };
};
